import { useCallback } from 'react';
import invariant from 'tiny-invariant';
import { v4 as uuidv4 } from 'uuid';

import { useCopilotActions, useMessages, useSessionId } from './copilotStore';
import { useSendCopilotMessageMutation } from './mutations/useSendCopilotMessageMutation';

export const useCopilotChat = () => {
  const sessionId = useSessionId();
  const messages = useMessages();
  const {
    addUserMessage,
    startAssistantMessage,
    finishAssistantMessage,
    startNewSession,
    setSessionId,
    removeLastMessage,
  } = useCopilotActions();

  const sendCopilotMessageMutation = useSendCopilotMessageMutation();

  const sendMessage = useCallback(
    (content: string) => {
      addUserMessage({ id: uuidv4(), content });

      const assistantMessageId = uuidv4();
      startAssistantMessage({ id: assistantMessageId });

      sendCopilotMessageMutation.mutate(
        {
          sessionId,
          userQuery: content,
        },
        {
          onSuccess: (data) => {
            invariant(data, 'Copilot response is required');
            setSessionId(data.sessionId);
            finishAssistantMessage({
              id: assistantMessageId,
              content: data.response ?? undefined,
              status: 'success',
            });
          },
          onError: () => {
            finishAssistantMessage({
              id: assistantMessageId,
              content: 'Sorry, I encountered an error. Please try again.',
              status: 'error',
            });
          },
        },
      );
    },
    [
      addUserMessage,
      startAssistantMessage,
      sendCopilotMessageMutation,
      sessionId,
      setSessionId,
      finishAssistantMessage,
    ],
  );

  const retryLastMessage = () => {
    const lastUserMessage = [...messages]
      .reverse()
      .find((msg) => msg.role === 'user');

    if (!lastUserMessage?.content) return;

    removeLastMessage();
    sendMessage(lastUserMessage.content);
  };

  const lastMessage = messages[messages.length - 1];
  const hasError = lastMessage?.status === 'error';

  return {
    messages,
    isRequesting: sendCopilotMessageMutation.isPending,
    sendMessage,
    startNewSession,
    retryLastMessage,
    hasError,
  };
};
